<template>
  <div class="table-container">
    <el-table
      class="interview-management-table"
      row-key="uuid"
      ref="interviewTableRef"
      tooltip-effect="light"
      max-height="688"
      :data="data"
      :header-row-style="{ height: '60px' }"
      :header-cell-style="{ background: '#f9f9f9' }"
      :default-expand-all-rows="defaultExpandAll"
      :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
      @row-click="handleRowClick"
    >
      <!-- 面试时间 -->
      <el-table-column prop="interviewDate"  class-name="interview-date" :min-width="tableColumnWidthMap.interviewDate" :label="$t(`${i18nPrefix}.label.interviewDate`)">
        <template #default="{ row }">
          <div class="time-info">
            <div class="date">{{ row.interviewDate }}</div>
            <div class="time">{{ row.interviewTime }}</div>
          </div>
        </template>
      </el-table-column>
      <!-- 公司 -->
      <el-table-column v-if="showCompany" show-overflow-tooltip prop="companyName" :min-width="tableColumnWidthMap.companyName" :label="$t(`${i18nPrefix}.label.entity`)"></el-table-column>
      <!-- 职位 -->
      <el-table-column v-if="showJob" show-overflow-tooltip prop="jobName" :min-width="tableColumnWidthMap.jobName" :label="$t(`${i18nPrefix}.label.job`)"></el-table-column>
      <!-- 招聘人员 -->
      <el-table-column prop="recruiter" show-overflow-tooltip :min-width="tableColumnWidthMap.recruiter" :label="$t(`${i18nPrefix}.label.organizer`)">
      </el-table-column>
      <!-- 招聘经理 -->
      <el-table-column prop="hiringManagerNames" show-overflow-tooltip :min-width="tableColumnWidthMap.hiringManagerNames" :label="$t(`${i18nPrefix}.label.interviewers`)">
      </el-table-column>
      <!-- 求职者 -->
      <el-table-column prop="candidateNames" show-overflow-tooltip :min-width="tableColumnWidthMap.candidateNames" :label="$t(`${i18nPrefix}.label.candidateNames`)">
        <template #default="{ row }">
          <el-tooltip v-if="row.is_group_interview" effect="light" popper-class="group-candidate-tooltip" placement="bottom">
            <div slot="content" class="group-candidate-list">
              <div v-for="(candidate, index) in row.candidate" class="group-candidate-item" :key="candidate.id">{{ index + 1 }}. {{ candidate.name }}</div>
            </div>
            <el-link class="candidate-names group-name" type="primary" :underline="false" :disabled="isCanceledInterview(row)" @click.stop="handleViewGroupInterview(row)">
              {{ row.candidateNames }}
            </el-link>
          </el-tooltip>
          <div v-else class="candidate-names" :class="(row.candidate && row.candidate.length === 1 && !isProfile) ? 'single-candidate' : ''" @click="handleCandidateNameClick(row)">{{ row.candidateNames }}</div>
        </template>
      </el-table-column>
      <!-- 状态 -->
      <el-table-column prop="status" class-name="status" :min-width="tableColumnWidthMap.status" :label="$t(`${i18nPrefix}.label.interviewStatus`)">
        <template #default="{ row }">
          <template v-if="row.status">
            <el-tooltip v-if="row.hire_manager.length > 1" effect="light" popper-class="light-tooltip-popper" placement="bottom">
              <div v-auto-tooltip="{content: getStatusLabel(row.status), placement: 'top'}" class="interview-status" :class="getStatusClass(row.status)">{{ getStatusLabel(row.status) }}</div>
              <template #content>
                <div v-for="hmRecord in row.hire_manager" class="status-tooltip" :key="hmRecord.id">
                  <span class="hm-name">{{ hmRecord.full_name }}</span>
                  <span class="hm-record-status">{{ getStatusLabel(hmRecord.record_status) }}</span>
                  <div v-if="hmRecord.record_status === INTERVIEW_STATUS.RESCHEDULING_BY_INTERVIEWER" class="hm-suggest-time">
                    <span v-for="(suggestTime, index) in hmRecord.available_interview_time_list" class="hm-suggest-time-item" :key="index">
                      {{ formatDate(suggestTime) }}
                    </span>
                  </div>
                </div>
              </template>
            </el-tooltip>
            <el-tooltip v-else effect="light" popper-class="light-tooltip-popper" placement="top">
              <div class="interview-status" :class="getStatusClass(row.status)">{{ getStatusLabel(row.status) }}</div>
              <template #content>
                <div class="status-tooltip">
                  <span class="status">{{ getStatusLabel(row.status) }}</span>
                  <div v-if="row.status === INTERVIEW_STATUS.RESCHEDULING_BY_INTERVIEWER" class="status-tooltip">
                    <div v-for="hmRecord in row.hire_manager" class="status-tooltip" :key="hmRecord.id">
                      <div v-if="hmRecord.record_status === INTERVIEW_STATUS.RESCHEDULING_BY_INTERVIEWER" class="hm-suggest-time">
                        <span v-for="(suggestTime, index) in hmRecord.available_interview_time_list" class="hm-suggest-time-item" :key="index">
                          {{ formatDate(suggestTime) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </el-tooltip>
          </template>
        </template>
      </el-table-column>
      <!-- 面试结果 -->
      <el-table-column prop="interview_decision" :min-width="tableColumnWidthMap.interviewResult" :label="$t(`${i18nPrefix}.label.interviewResult`)">
        <template #default="{ row }">
          <el-tooltip
            v-if="row.hmResultDetails && row.hmResultDetails.length > 1"
            placement="top"
            effect="light"
            popper-class="interview-result-tooltip"
          >
            <div class="interview-result-with-popover" slot="content">
              <div
                v-for="(hmResult, index) in row.hmResultDetails"
                class="hm-result-item"
                :key="index"
              >
                <span class="hm-name">{{ hmResult.hmName }}:</span>
                <span class="hm-decision" :class="getDecisionClass(hmResult.decision)">
                  {{ getDecisionLabel(hmResult.decision) }}
                </span>
              </div>
            </div>
            <div class="interview-result" :class="getInterviewResultClass(row.interview_decision)" :style="{color: getInterviewResultColor(row.interview_decision)}">
              {{ getInterviewResultLabel(row.interview_decision) }}
            </div>
          </el-tooltip>
          <template v-else>
            <div class="interview-result" :class="getInterviewResultClass(row.interview_decision)" :style="{color: getInterviewResultColor(row.interview_decision)}">
              {{ getInterviewResultLabel(row.interview_decision) }}
            </div>
          </template>
        </template>
      </el-table-column>
      <!-- 操作 -->
      <el-table-column v-if="hasInterviewPermission" show-overflow-tooltip prop="action" class-name="single-action" :min-width="tableColumnWidthMap.action" :label="$t(`${i18nPrefix}.label.action`)">
        <template #default="{ row }">
          <template v-if="[INTERVIEW_STATUS.CANCELED_BY_INTERVIEWER, INTERVIEW_STATUS.CANCELED_BY_ORGANIZER, INTERVIEW_STATUS.CANCELED_BY_CANDIDATE].includes(row.status) || row.hiddenAction">-</template>
          <el-dropdown v-else :ref="`actionDropdown_${row.uuid}`" :hide-on-click="false" class="action-dropdown" trigger="click" @command="handleAction($event, row)">
            <el-button v-if="row.noAction && !row.is_group_interview" v-auto-tooltip.always="row.disabledTooltip" disabled class="dropdown-btn" @click.stop>
              <span>{{ $t('common.label.operate') }}</span>
              <i class="dropdown-btn-icon el-icon-more"></i>
            </el-button>
            <el-button v-else class="dropdown-btn" :disabled="isDisabled && !row.is_group_interview" @click.stop>
              <span>{{ $t('common.label.operate') }}</span>
              <i class="dropdown-btn-icon el-icon-more"></i>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu class="no-arrow-dropdown-menu">
                <el-dropdown-item v-if="row.is_group_interview" command="viewGroupInterview">{{ $t(`${i18nPrefix}.btn.viewGroupInterview`) }}</el-dropdown-item>
                <template v-if="!row.noAction && !isDisabled">
                  <template v-if="row.status === INTERVIEW_STATUS.PENDING || row.status === INTERVIEW_STATUS.PARTIALLY_RATED">
                    <el-dropdown-item command="reschedule">{{ $t(`${i18nPrefix}.btn.reschedule`) }}</el-dropdown-item>
                    <el-dropdown-item command="sendInterviewRating">{{ $t(`${i18nPrefix}.btn.sendInterviewRating`) }}</el-dropdown-item>
                  </template>
                  <template v-else-if="row.status === INTERVIEW_STATUS.FINISHED">
                    <el-dropdown-item command="sendNew" @click="handleSendNew(row)">{{ $t(`${i18nPrefix}.btn.scheduleAnotherInterview`) }}</el-dropdown-item>
                  </template>
                  <template v-else-if="row.status === INTERVIEW_STATUS.PENDING_GENERATE_TEAMS_URL || row.status === INTERVIEW_STATUS.PENDING_GENERATE_TEAMS_EVENT">
                    <el-dropdown-item command="confirmInterview">{{ $t(`${i18nPrefix}.btn.interviewConfirm`) }}</el-dropdown-item>
                  </template>
                  <template v-else>
                    <el-dropdown-item v-if="row.can_edit" command="edit">{{ $t(`${i18nPrefix}.btn.editor`) }}</el-dropdown-item>
                    <el-popconfirm :title="$t(`${i18nPrefix}.message.confirmToCancelInterview`)" @confirm="handleCancel(row)">
                      <template #reference>
                        <el-dropdown-item command="cancel">{{ $t(`${i18nPrefix}.btn.cancel`) }}</el-dropdown-item>
                      </template>
                    </el-popconfirm>
                  </template>
                </template>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </el-table-column>
      <el-table-column v-if="false" show-overflow-tooltip prop="action" class-name="action" :min-width="tableColumnWidthMap.action" :label="$t(`${i18nPrefix}.label.action`)">
        <template #default="{ row }">
          <template v-if="row.status === INTERVIEW_STATUS.PENDING || row.status === INTERVIEW_STATUS.PARTIALLY_RATED">
            <el-button class="action-btn" type="text" @click="handleResend(row)">{{ $t(`${i18nPrefix}.btn.reschedule`) }}</el-button>
            <el-divider direction="vertical"></el-divider>
            <el-dropdown trigger="click" @command="handleCommand(row, $event)">
              <span class="dropdown-link">
                <span class="more">{{ $t(`${i18nPrefix}.btn.more`) }}</span>
                <i class="el-icon-arrow-down"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="sendInterviewRating">{{ $t(`${i18nPrefix}.btn.sendInterviewRating`) }}</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
          <template v-if="row.status === INTERVIEW_STATUS.SCHEDULED">
            <el-button v-if="row.can_edit" class="action-btn" type="text" @click="handleEdit(row)">{{ $t(`${i18nPrefix}.btn.editor`) }}</el-button>
            <el-divider v-if="row.can_edit" direction="vertical"></el-divider>
            <el-popconfirm :title="$t(`${i18nPrefix}.message.confirmToCancelInterview`)" @confirm="handleCancel(row)">
              <template #reference>
                <el-button class="action-btn" type="text">{{ $t(`${i18nPrefix}.btn.cancel`) }}</el-button>
              </template>
            </el-popconfirm>
          </template>
          <template v-if="row.status === INTERVIEW_STATUS.FINISHED">
            <el-button class="action-btn" type="text" @click="handleSendNew(row)">{{ $t(`${i18nPrefix}.btn.scheduleAnotherInterview`) }}</el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>
    <RectLoading v-show="loading" class="loading"/>
  </div>
</template>

<script>
import _ from 'lodash'
import { INTERVIEW_CANCELED_STATUS, INTERVIEW_RATING_DECISION, INTERVIEW_STATUS, RATE_STATUS } from '@/constant/interview'
import RectLoading from '@/components/loading/rectLoading.vue'
import moment from 'moment'

export default {
  components: { RectLoading },
  props: {
    data: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    showCompany: {
      type: Boolean,
      default: false
    },
    showJob: {
      type: Boolean,
      default: false
    },
    isScrollable: {
      type: Boolean,
      default: false
    },
    tableColumnWidthMap: {
      type: Object,
      required: true
    },
    singleActionBtn: {
      type: Boolean,
      default: false
    },
    isProfile: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    defaultExpandAll: {
      type: Boolean,
      default: false
    }
  },
  metaInfo () {
    return {
      style: [
        {
          innerHTML: `.el-tooltip__popper.is-light {
            border-color: #e6e6e6;
            background: #e6e6e6;
            -webkit-box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
                    box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow::after {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }`
        }
      ]
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      INTERVIEW_STATUS,
      RATE_STATUS,
      emitSearch: null,
      search: {
        keywords: null,
        status: null
      },
      emitScrollBottom: null,
      atBottom: false
    }
  },
  computed: {
    hasInterviewPermission () {
      return this.hasAnyPermission(['PREMIUM_INTERVIEW_SCHEDULING', 'PERM_HR_INTERVIEW_SCHEDULING'])
    }
  },
  created () {
    this.emitSearch = _.debounce(() => {
      this.$emit('search', this.search)
    }, 500)
    this.emitScrollBottom = _.debounce(() => {
      this.$emit('scrollBottom')
    }, 500)
  },
  mounted () {
    const el = this.$el.querySelector('.el-table__body-wrapper')
    el.addEventListener('scroll', () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight - 10
      if (distance <= 50) {
        if (!this.atBottom) {
          this.emitScrollBottom()
        }
        this.atBottom = true
      } else {
        this.atBottom = false
      }
    })
  },
  methods: {
    handleCommand (record, command) {
      if (command === 'sendInterviewRating') {
        this.handleSendRating(record)
      }
    },
    handleEdit (record) {
      this.$emit('edit', record)
    },
    handleCancel (record) {
      this.$emit('cancel', record)
    },
    handleResend (record) {
      this.$emit('resend', record)
    },
    handleSendNew (record) {
      this.$emit('sendNew', record)
    },
    handleConfirmInterview (record) {
      this.$emit('confirmInterview', record)
    },
    handleSendRating (record) {
      this.$emit('sendRating', record)
    },
    handleAction (command, row) {
      if (command === 'viewGroupInterview') {
        this.handleViewGroupInterview(row)
        this.$refs[`actionDropdown_${row.uuid}`].hide()
        return
      }
      if (row.noAction || this.isDisabled) {
        return
      }
      if (command === 'reschedule') {
        this.handleResend(row)
      }
      if (command === 'sendInterviewRating') {
        this.handleSendRating(row)
        this.$refs[`actionDropdown_${row.uuid}`].hide()
      }
      if (command === 'sendNew') {
        this.handleSendNew(row)
      }
      if (command === 'edit') {
        this.handleEdit(row)
      }
      if (command === 'confirmInterview') {
        this.handleConfirmInterview(row)
      }
    },
    handleCandidateNameClick (row) {
      if (row.candidate?.length !== 1) {
        return
      }
      this.$emit('openCandidateDetail', row)
    },
    isCanceledInterview (row) {
      return _.includes(INTERVIEW_CANCELED_STATUS, row.status)
    },
    handleViewGroupInterview (row) {
      if (this.isCanceledInterview(row)) {
        return
      }
      this.$emit('viewGroupInterview', row)
    },
    handleRowClick (row, column) {
      if (row.children && row.children.length > 0) {
        this.$refs.interviewTableRef.toggleRowExpansion(row)
      }
    },
    getStatusClass (status) {
      if (status === INTERVIEW_STATUS.PARTIALLY_RATED) {
        return 'partially-rated'
      }
      if (status === INTERVIEW_STATUS.RESCHEDULING_BY_INTERVIEWER) {
        return 'rescheduling-by-interviewer'
      }
      const statusLabelKey = _.findKey(INTERVIEW_STATUS, value => value === status)
      return _.toLower(statusLabelKey)
    },
    getStatusLabel (status) {
      const statusLabelKey = _.findKey(INTERVIEW_STATUS, value => value === status)
      return this.$t(`${this.i18nPrefix}.label.${statusLabelKey}`)
    },
    getStatusTooltip (interview) {
      let tip = this.getStatusLabel(interview.status)
      if (interview.status === INTERVIEW_STATUS.RESCHEDULING_BY_INTERVIEWER) {
        tip = ''
      }
      return tip
    },
    getDecisionClass (decision) {
      if (decision === RATE_STATUS.RECOMMENDED) {
        return 'decision-approved'
      } else if (decision === RATE_STATUS.NOT_RECOMMENDED) {
        return 'decision-rejected'
      } else if (decision === RATE_STATUS.PENDING) {
        return 'decision-undetermined'
      } else if (decision === RATE_STATUS.NO_RECOMMENDATION) {
        return 'decision-pending'
      }
      return 'decision-pending'
    },
    getDecisionLabel (decision) {
      if (decision === RATE_STATUS.RECOMMENDED) {
        return this.$t(`${this.i18nPrefix}.label.RECOMMENDED`)
      } else if (decision === RATE_STATUS.NOT_RECOMMENDED) {
        return this.$t(`${this.i18nPrefix}.label.NOT_RECOMMENDED`)
      } else if (decision === RATE_STATUS.PENDING) {
        return this.$t(`${this.i18nPrefix}.label.RATE_PENDING`)
      } else if (decision === RATE_STATUS.NO_RECOMMENDATION) {
        return this.$t(`${this.i18nPrefix}.label.NO_RECOMMENDATION`)
      }
      return this.$t(`${this.i18nPrefix}.label.RATE_PENDING`)
    },
    /**
     * 获取面试结果的样式类
     */
    getInterviewResultClass (result) {
      switch (result) {
        case INTERVIEW_RATING_DECISION.CONFLICT:
          return 'split-decision'
        case INTERVIEW_RATING_DECISION.RECOMMENDED:
          return 'approved'
        case INTERVIEW_RATING_DECISION.NOT_RECOMMENDED:
          return 'rejected'
        case INTERVIEW_RATING_DECISION.PENDING:
          return 'undetermined'
        case INTERVIEW_RATING_DECISION.NO_RECOMMENDATION:
          return 'undetermined'
        default:
          return 'not-yet'
      }
    },
    getInterviewResultColor (result) {
      // yellow
      let color = '#F4A958'
      if (result === INTERVIEW_RATING_DECISION.NOT_RECOMMENDED) {
        // red
        color = '#E27676'
      } else if (result === INTERVIEW_RATING_DECISION.RECOMMENDED) {
        // green
        color = '#51BF82'
      }
      return color
    },
    /**
     * 获取面试结果的文本标签
     */
    getInterviewResultLabel (result) {
      switch (result) {
        case INTERVIEW_RATING_DECISION.CONFLICT:
          return this.$t(`${this.i18nPrefix}.label.SPLIT_DECISION`)
        case INTERVIEW_RATING_DECISION.RECOMMENDED:
          return this.$t(`${this.i18nPrefix}.label.RECOMMENDED`)
        case INTERVIEW_RATING_DECISION.NOT_RECOMMENDED:
          return this.$t(`${this.i18nPrefix}.label.NOT_RECOMMENDED`)
        case INTERVIEW_RATING_DECISION.PENDING:
          return this.$t(`${this.i18nPrefix}.label.RATE_PENDING`)
        case INTERVIEW_RATING_DECISION.NO_RECOMMENDATION:
          return this.$t(`${this.i18nPrefix}.label.NO_RECOMMENDATION`)
        default:
          return this.$t(`${this.i18nPrefix}.label.NOT_YET_RATE`)
      }
    },
    formatDate (dateTime) {
      const startDate = dateTime.startDate
      const startTime = dateTime.startTime
      const endTime = dateTime.endTime
      const startDateTime = moment(`${startDate} ${startTime}`).format('YYYY-MM-DD HH:mm')
      return `${startDateTime}-${moment(endTime, 'HH:mm:ss').format('HH:mm')}`
    }
  },
  watch: {
    data: {
      handler () {
        const tableRef = this.$refs.interviewTableRef
        tableRef.layout.updateElsHeight()
      }
    },
    search: {
      deep: true,
      handler () {
        this.emitSearch()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.table-container {
  position: relative;

  .interview-management-table {
    border-bottom: 0;

    .dropdown-link {
      cursor: pointer;

      .el-icon-arrow-down {
        margin-left: 5px;
      }
    }

    .single-candidate {
      cursor: pointer;
      color: #0096cc;
    }

    .action-btn, .more, .el-icon-arrow-down {
      color: #0096cc;
    }

    .candidate-names {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .group-name {
      display: block;
      width: 100%;
      text-align: left;
    }

    .interview-status {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .interview-result {
      word-break: break-word;
    }

    .status {
      .cell {
        > * {
          word-break: break-word;
        }
      }
    }

    .action {
      .cell {
        > * {
          word-break: break-word;
        }
      }
    }

    .single-action {
      .cell {
        > * {
          word-break: break-word;
        }
      }

      .action-dropdown {
        .dropdown-btn {
          padding: 6px 14px;
          font-size: 14px;
          line-height: 18px;
          border-color: #0096cc;
          color: #0096cc;
          font-weight: 500;
          border-radius: 2px;

          .dropdown-btn-icon {
            margin-left: 5px;
            transform: rotate(90deg);
          }

          &.is-disabled {
            color: #c0c4cc;
            border-color: #ebeef5;
          }
        }
      }
    }

    .pending {
      color: #f4a958;
    }

    .scheduled {
      color: #51abff;
    }

    .finished {
      color: #51bf82;
    }

    .canceled {
      color: #909399;
    }

    .partially-rated {
      color: #f4a958;
    }

    .rescheduling-by-interviewer {
      color: #f4a958;
    }

    .hm-name {
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    ::v-deep {
      .el-table__header {
        tr {
          th {
            &:first-child {
              padding-left: 40px;
            }

            .cell {
              word-break: break-word;
            }
          }
        }
      }

      .el-table__body {
        .el-table__row {
          .el-table__cell:first-child {
            padding-left: 40px;
          }

          .interview-date {
            .cell {
              display: flex;
              align-items: center;

              .el-table__expand-icon {
                position: absolute;
                left: 16px;
              }
            }
          }
        }
      }
    }
  }

  .loading {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
  }
}

.group-candidate-list {
  max-width: 480px;

  .group-candidate-item {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.status-tooltip {
  display: flex;
  align-items: center;

  .hm-name {
    max-width: 100px;
    white-space: break-spaces;
    word-break: break-word;
  }

  .hm-record-status {
    padding: 0 4px;
    width: 100px;
    white-space: break-spaces;
    word-break: break-word;
  }

  .hm-suggest-time {
    display: flex;
    flex-direction: column;
  }

  & + .status-tooltip {
    margin-top: 4px;
  }
}
</style>
